import React, { useEffect, useState } from "react";
import { Pencil, Trash2, Plus, Calendar, Image as ImageIcon } from "lucide-react";
import Sidebar from "../../../component/includes/sidebar";
import {
  OrganizationStructureContent,
  organizationStructureService,
  UpdateOrganizationStructureData,
} from "../../../services/Tentang/StrukturOrganisasi";

const OrganizationStructureManagement = () => {
  const [structures, setStructures] = useState<OrganizationStructureContent[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState<boolean>(false);
  const [editingStructure, setEditingStructure] = useState<OrganizationStructureContent | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchStructures();
  }, []);

  const fetchStructures = async () => {
    setLoading(true);
    try {
      const response = await organizationStructureService.getOrganizationStructures();
      setStructures(Array.isArray(response) ? response : []);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to fetch organization structures");
      setIsErrorModalOpen(true);
      console.error("Error fetching structures:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setError("Please select a valid image file");
      setIsErrorModalOpen(true);
    }
  };

  const handleCreateStructure = async () => {
    if (!selectedFile) {
      setError("Please select an image");
      setIsErrorModalOpen(true);
      return;
    }

    try {
      await organizationStructureService.createOrganizationStructure({ image: selectedFile });
      await fetchStructures();
      setSuccessMessage("Struktur organisasi berhasil ditambahkan");
      setIsSuccessModalOpen(true);
      resetForm();
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to create structure");
      setIsErrorModalOpen(true);
    }
  };

  const handleUpdateStructure = async () => {
    if (!editingStructure) return;

    try {
      const updateData: UpdateOrganizationStructureData = {
        id: editingStructure.id,
        image: selectedFile || undefined,
      };
      await organizationStructureService.updateOrganizationStructure(updateData);
      await fetchStructures();
      setSuccessMessage("Struktur organisasi berhasil diperbarui");
      setIsSuccessModalOpen(true);
      resetForm();
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to update structure");
      setIsErrorModalOpen(true);
    }
  };

  const handleDeleteStructure = async (id: number) => {
    if (!window.confirm("Apakah Anda yakin ingin menghapus struktur organisasi ini?")) return;

    try {
      await organizationStructureService.deleteOrganizationStructure(id);
      await fetchStructures();
      setSuccessMessage("Struktur organisasi berhasil dihapus");
      setIsSuccessModalOpen(true);
    } catch (err: any) {
      setError(err?.error || err?.message || "Failed to delete structure");
      setIsErrorModalOpen(true);
    }
  };

  const resetForm = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setEditingStructure(null);
    setIsModalOpen(false);
  };

  const totalStructures = structures.length;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-4 border-blue-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <Sidebar/>

      <div className="flex-1 flex flex-col">
<div className="flex-1 p-4 md:p-6 mt-14">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <div className="bg-white rounded-3xl shadow-sm p-6 mb-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-500 to-indigo-600 rounded-2xl flex items-center justify-center">
                  <span className="text-white text-3xl">🖼️</span>
                </div>
                <div>
                  <h1 className="text-3xl font-semibold text-gray-900">Manajemen Struktur Organisasi</h1>
                  <p className="text-gray-500 text-sm mt-1">Kelola konten struktur organisasi perusahaan Anda</p>
                </div>
              </div>

              <button
                onClick={() => {
                  setIsModalOpen(true);
                  setEditingStructure(null);
                  setSelectedFile(null);
                  setPreviewUrl(null);
                }}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-2xl font-medium transition-all active:scale-95"
              >
                <Plus className="w-5 h-5" />
                Tambah Struktur Baru
              </button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-3xl shadow-sm overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 border-b">
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-20">ID</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600">GAMBAR</th>
                    <th className="px-8 py-5 text-left font-medium text-gray-600 w-52">DIPERBARUI</th>
                    <th className="px-8 py-5 text-right font-medium text-gray-600 w-32">AKSI</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {structures.length > 0 ? (
                    structures.map((structure) => (
                      <tr key={structure.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-8 py-6">
                          <div className="w-9 h-9 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center font-semibold text-lg">
                            {structure.id}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <img
                            src={`${process.env.REACT_APP_API_URL || ''}${structure.image}`}
                            alt="Struktur Organisasi"
                            className="w-20 h-20 object-cover rounded-lg border border-gray-200"
                          />
                        </td>
                        <td className="px-8 py-6 text-gray-500">
                          <div className="flex items-center gap-2">
                            <Calendar className="w-4 h-4" />
                            {new Date(structure.updated_at).toLocaleDateString('id-ID', {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            })}
                          </div>
                        </td>
                        <td className="px-8 py-6">
                          <div className="flex items-center justify-end gap-4">
                            <button
                              onClick={() => {
                                setEditingStructure(structure);
                                setPreviewUrl(`${process.env.REACT_APP_API_URL || ''}${structure.image}`);
                                setIsModalOpen(true);
                              }}
                              className="p-3 text-blue-500 hover:bg-blue-50 rounded-2xl transition-all hover:scale-110"
                            >
                              <Pencil className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleDeleteStructure(structure.id)}
                              className="p-3 text-red-500 hover:bg-red-50 rounded-2xl transition-all hover:scale-110"
                            >
                              <Trash2 className="w-5 h-5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={4} className="py-20">
                        <div className="flex flex-col items-center justify-center text-center">
                          <ImageIcon className="w-16 h-16 text-gray-300 mb-4" />
                          <p className="text-gray-500 text-lg font-medium">
                            Tidak ada struktur organisasi ditemukan
                          </p>
                          <p className="text-gray-400 text-sm mt-1">
                            Mulai dengan menambahkan struktur pertama Anda
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Total Footer */}
            <div className="bg-white rounded-3xl shadow-sm mt-6 p-6 text-center">
              <p className="text-gray-600 font-medium">
                Total Struktur: <span className="text-gray-900">{totalStructures}</span>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Modal Upload Gambar */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl p-8 w-full max-w-md">
            <h2 className="text-2xl font-semibold mb-6">
              {editingStructure ? "Edit Struktur Organisasi" : "Tambah Struktur Baru"}
            </h2>

            <div className="border-2 border-dashed border-gray-300 rounded-2xl p-6 text-center">
              <input
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
                id="structure-image"
              />
              <label htmlFor="structure-image" className="cursor-pointer block">
                {previewUrl ? (
                  <img src={previewUrl} alt="Preview" className="mx-auto max-h-64 rounded-lg" />
                ) : (
                  <div className="py-8">
                    <ImageIcon className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                    <p className="text-gray-600">Klik untuk upload gambar</p>
                  </div>
                )}
              </label>
            </div>

            <div className="flex gap-4 mt-8">
              <button
                onClick={() => setIsModalOpen(false)}
                className="flex-1 py-4 border border-gray-300 rounded-2xl font-medium hover:bg-gray-50 transition"
              >
                Batal
              </button>
              <button
                onClick={editingStructure ? handleUpdateStructure : handleCreateStructure}
                className="flex-1 py-4 bg-purple-600 hover:bg-purple-700 text-white rounded-2xl font-medium transition-all"
                disabled={!selectedFile && !editingStructure}
              >
                {editingStructure ? "Perbarui" : "Tambah"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Success & Error Modals */}
      {isSuccessModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-green-600 mb-4">Berhasil</h2>
            <p className="text-gray-700 mb-6">{successMessage}</p>
            <button onClick={() => setIsSuccessModalOpen(false)} className="bg-green-500 text-white px-10 py-3 rounded-2xl hover:bg-green-600">
              OK
            </button>
          </div>
        </div>
      )}

      {isErrorModalOpen && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/60 p-4">
          <div className="bg-white rounded-3xl p-8 max-w-md w-full text-center">
            <h2 className="text-2xl font-bold text-red-600 mb-4">Error</h2>
            <p className="text-gray-700 mb-6">{error}</p>
            <button onClick={() => setIsErrorModalOpen(false)} className="bg-red-500 text-white px-10 py-3 rounded-2xl hover:bg-red-600">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationStructureManagement;